import { PaymentResult } from '@pos-common/classes/payment-result.class';
import { Injectable } from '@angular/core';
import { PAYMENT_PROVIDERS } from '@pos-common/constants/payment-providers.const';
import {
  CommonRequestOptions,
  MakePaymentOptions,
  MakePaymentRequestOptions,
  MakePaymentResponse,
  PaymentType,
  TransactionStatusRequestOptions,
} from '../adyen-types';
import { AdyenPaymentApi } from './api/adyen-payment-api.service';
import { PaymentProcessingService } from '@pos-common/components/payment-processing/payment-processing.service';
import { PaymentProcessingActions } from '@pos-common/components/payment-processing/payment-processing-actions.enum';
import { AdyenUtils } from '@pos-common/services/system/adyen/adyen-utils';
import { LogService } from '../../logger/log.service';
import { AdyenPaymentErrors } from '../costants';
import { TranslateService } from '@ngx-translate/core';
import { ErrorTranslationHelper } from '@pos-common/classes';
import { DefaultPaymentMethods } from '@pos-common/constants';
import { SetTimeoutUtil } from '@pos-common/services/utils/settimeout.utils';
import { UuidService } from '@pos-common/services/utils/uuid.utils';
import { AdyenPaymentError } from '@pos-common/services/system/adyen/classes';
import { AdyenErrorPaymentActions } from './../interfaces';
import { AdyenSoftPosService } from '@pos-common/services/system/adyen/services/adyen-soft-pos.service';
import { ErrorCondition } from '@pos-common/services/system/adyen/enums';

type MakeTransactionOptions = { type: PaymentType } & MakePaymentOptions;

@Injectable()
export class AdyenPaymentService {
  private readonly errorTranslationHelper = new ErrorTranslationHelper();
  private readonly logger = this.logService.createLogger('AdyenPaymentService');

  constructor(
    public UuidService: UuidService,
    private readonly adyenPaymentApi: AdyenPaymentApi,
    private readonly paymentProcessingService: PaymentProcessingService,
    private readonly adyenSoftPosService: AdyenSoftPosService,
    private readonly utils: AdyenUtils,
    private readonly logService: LogService,
    private readonly setTimeoutUtil: SetTimeoutUtil,
    private readonly translateService: TranslateService
  ) {
    this.setupErrorMessages();
  }

  private setupErrorMessages() {
    this.errorTranslationHelper.setupMessages([
      [AdyenPaymentErrors.userCancel, 'terminal_transaction_error_user_cancel'],
      [AdyenPaymentErrors.provideTerminalInfo, 'settings_provide_terminal_info'],
      [AdyenPaymentErrors.currencyNotSupported, 'terminal_transaction_error_wrong_currency'],
      [AdyenPaymentErrors.refundNotEnabled, 'payment_method_does_not_support_refund'],
      [AdyenPaymentErrors.notEnoughBalance, 'terminal_transaction_error_not_enough_balance'],
      [AdyenPaymentErrors.NoAcquirerFound, 'terminal_transaction_payment_method_unavailable'],
      [AdyenPaymentErrors.declinedOnline, 'terminal_transaction_error_declined'],
      [AdyenPaymentErrors.notPresentCard, 'terminal_transaction_error_not_present_card'],
      [AdyenPaymentErrors.timeoutWaitingResponse, 'connection_timeout'],
      [AdyenPaymentErrors.applicationTimeout, 'connection_timeout'],
      [AdyenPaymentErrors.cardRemoved, 'terminal_transaction_error_user_cancel'],
      [AdyenPaymentErrors.failedToConnect, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.statusInProgress, 'terminal_error_connection_fail'],
      [AdyenPaymentErrors.connectFailedToPaymentHost, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.connectFailedToServer, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.requestTimeout, 'connection_timeout'],
      [AdyenPaymentErrors.timeout, 'terminal_error_connect_fail'],
      [AdyenPaymentErrors.wrongPin, 'terminal_error_wrong_pin'],
      [AdyenPaymentErrors.cardExpired, 'terminal_error_card_expired'],
      [AdyenPaymentErrors.missingAuthentication, 'Missing Authentication']
    ]);
  }

  makePayment(options: MakePaymentOptions): Promise<PaymentResult> {
    const { isPhysicalDevice } = options;
    return isPhysicalDevice ?
      this.prepareTransaction({ type: 'payment', ...options }) :
      this.makeSoftPosPayment(options);
  }

  makeRefund(options: MakePaymentOptions): Promise<PaymentResult> {
    const { isPhysicalDevice } = options;
    return isPhysicalDevice ?
      this.prepareTransaction({ type: 'refund', ...options }) :
      this.makeSoftPosRefund(options);
  }

  private prepareTransaction(options: MakeTransactionOptions) {
    return new Promise<PaymentResult>((resolve, reject) => {
      let transactionError: Error = null;
      const requestOptions: MakePaymentRequestOptions = this.getPaymentRequestOptions(options);
      this.paymentProcessingService.init(
        () => { // retry user action
            return this.isOnline()
              .then(() => this.retryTransaction(options))
              .then(resolve)
              .catch((error) => (transactionError = error))
          },
        () => reject(transactionError), // close user action
        () => {  // check user action
            return this.checkTransactionStatus(requestOptions)
              .then(resolve)
              .catch((error) => (transactionError = error))
          },
        options.type
      );
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
      this.isOnline()
        .then(() => this.makeTransaction(requestOptions))
        .then(resolve)
        .catch((error) => (transactionError = error));
    });
  }

  makeSoftPosRefund(options: MakePaymentOptions) {
    return this.makeSoftPosTransaction(options, 'refund');
  }

  makeSoftPosPayment(options: MakePaymentOptions) {
    return this.makeSoftPosTransaction(options, 'payment');
  }

  makeSoftPosTransaction(options: MakePaymentOptions, type: 'payment' | 'refund') {
    return new Promise<PaymentResult>((resolve, reject) => {
      let transactionError: Error = null;
      const optionsTransactions: MakeTransactionOptions = { type, ...options };
      const requestOptions: MakePaymentRequestOptions = this.getPaymentRequestOptions(optionsTransactions);
      const saleToPOIRequest = this.adyenPaymentApi.generateSaleToPOIRequest(requestOptions, 'softpos');
      this.paymentProcessingService.init(
        () => this.retryTransactionSoftPos(optionsTransactions)  // retry user action
              .then((data: PaymentResult): PaymentResult => data)
              .then(resolve)
              .catch(error => (transactionError = error)),
        () => reject(transactionError), // close user action
        () => {},
        optionsTransactions.type
      );
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
      this.transactionSoftPosProcess(saleToPOIRequest)
        .then((data: PaymentResult): PaymentResult => data)
        .then(resolve)
        .catch(error => (transactionError = error));
    });
  }

  private async finishTransaction(paymentResponse: MakePaymentResponse, payment: PAYMENT_PROVIDERS = PAYMENT_PROVIDERS.PAYMASH_PAY) {
    const paymentResult = new PaymentResult(payment);
    paymentResult.setPaymentResultData(paymentResponse);
    return paymentResult;
  }

  private makeTransaction(options: MakePaymentRequestOptions): Promise<unknown> {
    return this.adyenPaymentApi
      .makePayment(options)
      .then((paymentResponse: MakePaymentResponse): Promise<PaymentResult> => this.finishTransaction(paymentResponse))
      .catch((error: AdyenPaymentError) => {
        this.processTransactionErrors(error)
      });
  }

  private transactionSoftPosProcess(saleToPOIRequest) {
    return this.adyenSoftPosService.createPaymentToSoftPos(saleToPOIRequest)
      .then((paymentResponse: MakePaymentResponse): Promise<PaymentResult> => this.finishTransaction(paymentResponse))
      .catch((error: AdyenPaymentError) => {
        this.processTransactionErrors(error);
      })
  }

  private processTransactionErrors(error: AdyenPaymentError) {
    if (error.isLegalError) {
      const message: string  = error?.refusalReason + ' ' + error?.errorCondition + ' ' + error?.message;
      this.logger.info(message)
    } else {
      this.logger.error(error, 'Logger make transaction: ');
    }

    const { action, message: displayMessage, additionalButtonConfig } = this.getActionsAfterError(error);

    this.paymentProcessingService.dispatchAction(action, {
      message: this.translateService.instant(displayMessage),
      ...additionalButtonConfig
    });

    throw new AdyenPaymentError(error.message);
  }

  private getPaymentRequestOptions(options: MakeTransactionOptions): MakePaymentRequestOptions {
    const { currency, amount, paymentUuid, type, paymentMethod } = options;
    const requestOptions: MakePaymentRequestOptions = {
      ...this.utils.createCommonRequestOptions(),
      currency,
      amount,
      saleTransaction: {
        transactionID: paymentUuid, // "27908" -- your reference to identify a payment. We recommend using a unique value per payment. In your Customer Area and Adyen reports, this will show as the merchant reference for the transaction.
        timeStamp: new Date().toISOString(), // "2019-03-07T10:11:04+00:00" -- date and time of the request in UTC format.
      },
      type,
      allowedPaymentBrand: paymentMethod === DefaultPaymentMethods.TWINT ? 'twint_pos' : undefined,
    };
    return requestOptions;
  }

  private checkTransactionStatus(options: CommonRequestOptions) {
    this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing, {
      checkButtonOff: true
    });
    const requestOptions = this.getTransactionStatusRequestOptions(options);
    return this.adyenPaymentApi
      .checkTransactionStatus(requestOptions)
      .then((paymentResponse) => this.finishTransaction(paymentResponse))
      .catch((error) => {
        this.logger.error(error, 'checkTransactionStatus');

        const { action, message, additionalButtonConfig } = this.getActionsAfterError(error);

        this.paymentProcessingService.dispatchAction(action, {
          message: this.translateService.instant(message),
          ...additionalButtonConfig,
        });
        throw error;
      });
  }

  private retryTransaction(options: MakeTransactionOptions) {
    this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
    const requestOptions = this.getTransactionRetryRequestOptions(options);
    return this.makeTransaction(requestOptions);
  }

  private retryTransactionSoftPos(options: MakeTransactionOptions) {
    this.paymentProcessingService.dispatchAction(PaymentProcessingActions.processing);
    const requestOptions: MakePaymentRequestOptions = this.getTransactionRetryRequestOptions(options);
    const saleToPOIRequest = this.adyenPaymentApi.generateSaleToPOIRequest(requestOptions, 'softpos');
    return this.transactionSoftPosProcess(saleToPOIRequest);
  }

  private getTransactionStatusRequestOptions(options: CommonRequestOptions): TransactionStatusRequestOptions {
    return {
      ...this.utils.createCommonRequestOptions(),
      transactionSaleID: options.saleID,
      transactionServiceID: options.serviceID,
    };
  }

  private getTransactionRetryRequestOptions(options: MakeTransactionOptions): MakePaymentRequestOptions {
    const { currency, amount, type, paymentMethod } = options;
    const requestOptions: MakePaymentRequestOptions = {
      ...this.utils.createCommonRequestOptions(),
      currency,
      amount,
      saleTransaction: {
        transactionID: this.UuidService.generate(), // "27908" -- your reference to identify a payment. We recommend using a unique value per payment. In your Customer Area and Adyen reports, this will show as the merchant reference for the transaction.
        timeStamp: new Date().toISOString(), // "2019-03-07T10:11:04+00:00" -- date and time of the request in UTC format.
      },
      type,
      allowedPaymentBrand: paymentMethod === DefaultPaymentMethods.TWINT ? 'twint_pos' : undefined,
    };
    return requestOptions;
  }

  private async isOnline() {
    await this.setTimeoutUtil.waitTimeAndDo(1);
    const requestOptions = this.utils.createCommonRequestOptions();
    return this.adyenPaymentApi.diagnostic(requestOptions).catch((error) => {
      this.logger.error(error, 'diagnostic');
      this.paymentProcessingService.dispatchAction(PaymentProcessingActions.retry, {
        message: this.generateMessageError(PaymentProcessingActions.retry, error),
        retryButtonOff: true,
      });
      throw error;
    });
  }

  private getActionsAfterError(error: AdyenPaymentError): AdyenErrorPaymentActions {
    const { errorCondition } = error;

    const isRetryAction: boolean = this.isNeedToRetry(errorCondition);
    const isWithoutAdditionalButton: boolean = this.isWithOutUserOptions(errorCondition);


    const action = isRetryAction
      ? PaymentProcessingActions.retry
      : PaymentProcessingActions.check;

    const message: string = this.generateMessageError(action, error, errorCondition);

    const additionalButtonConfig: Record<string, boolean> = isRetryAction
      ? { retryButtonOff: isWithoutAdditionalButton }
      : { checkButtonOff: isWithoutAdditionalButton };

    return { action, message, additionalButtonConfig };
  }

  // TODO isTimeOutError() may be deprecated functionality !
  // @ts-ignore
  private isTimeOutError(translateKey: string): boolean {
    const timeoutErrors: string[] = [
      AdyenPaymentErrors.timeoutWaitingResponse,
      AdyenPaymentErrors.applicationTimeout,
      AdyenPaymentErrors.requestTimeout,
    ];

    return timeoutErrors
      .map((timeoutError) => this.errorTranslationHelper.getTranslationKey(timeoutError))
      .some((timeoutError) => timeoutError === translateKey);
  }

  private isNeedToRetry(errorCondition: ErrorCondition): boolean {
    const retryCases: ErrorCondition[] = [
      ErrorCondition.Refusal,
      ErrorCondition.InProgress,
      ErrorCondition.NotFound,
      ErrorCondition.InvalidCard,
      ErrorCondition.WrongPIN,
      ErrorCondition.NotAllowed
    ];

    return retryCases.includes(errorCondition);
  }

  private isWithOutUserOptions(errorCondition: ErrorCondition): boolean {
    const emptyOptionsCases: ErrorCondition[] = [
      ErrorCondition.Aborted,
      ErrorCondition.Busy,
      ErrorCondition.Cancel,
      ErrorCondition.DeviceOut,
      ErrorCondition.UnavailableService,
      ErrorCondition.DeviceNotSafe,
      ErrorCondition.SecurityBlock,
      ErrorCondition.SoftPosError
    ];

    return emptyOptionsCases.includes(errorCondition);
  }

  private isShowMessageWithoutTranslate(errorCondition: ErrorCondition): boolean {
    const setShowRawMessage: ErrorCondition[] = [
      ErrorCondition.UnavailableService,
      ErrorCondition.DeviceNotSafe,
      ErrorCondition.SecurityBlock,
      ErrorCondition.SoftPosError
    ]

    return setShowRawMessage.includes(errorCondition);
  }

  private generateMessageError(action: PaymentProcessingActions, error: AdyenPaymentError, errorCondition?: ErrorCondition): string {

    const isShowMessageWithoutTranslate: boolean = errorCondition ? this.isShowMessageWithoutTranslate(errorCondition) : false;

    const message: string =
      this.errorTranslationHelper.getTranslationKey(error?.keyError)
      || this.errorTranslationHelper.getTranslationKey(error?.message)
      || this.errorTranslationHelper.getTranslationKey(error?.refusalReason)
      || (isShowMessageWithoutTranslate ? this.errorTranslationHelper.threatModelChecker(error) : 'adyen_payment_process_common_error_msg');

    const isWithActionButton: boolean = !this.isWithOutUserOptions(error.errorCondition);

    return (action === PaymentProcessingActions.check && isWithActionButton) ? 'transaction_check_status_message' : message;
  }
}
